import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  Check, 
  CheckCheck, 
  Clock, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle, 
  Info, 
  ExternalLink,
  Star,
  Scale,
  Calendar,
  X,
  User,
  Phone,
  FileText,
  MessageSquare
} from 'lucide-react';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { formatDate } from '../../utils/helpers';
import ClientChatModal from '../chat/ClientChatModal';

export default function NotificationBell({ theme = 'dark', onAuthClick = null }) {
  const { user, unreadNotifications, setUnreadNotifications, isAuthenticated } = useAuth();
  const { t, currentLang } = useLanguage();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [chatUnread, setChatUnread] = useState({ count: 0, items: [] });
  const [activeChatConvId, setActiveChatConvId] = useState(null);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const isLight = theme === 'light';

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchChatSummary = async () => {
    if (!isAuthenticated) return;
    try {
      const res = await api.get('/chat/unread-summary');
      if (res?.success) {
        setChatUnread({
          count: res.unreadCount || 0,
          items: res.recentUnread || [],
        });
      }
    } catch (err) {}
  };

  const fetchNotifications = async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const [notifRes, chatRes] = await Promise.all([
        api.get('/notifications/my'),
        api.get('/chat/unread-summary').catch(() => null),
      ]);
      if (notifRes?.success) {
        setNotifications(notifRes.notifications || []);
        if (typeof notifRes.unreadCount === 'number') {
          setUnreadNotifications(notifRes.unreadCount);
        }
      }
      if (chatRes?.success) {
        setChatUnread({
          count: chatRes.unreadCount || 0,
          items: chatRes.recentUnread || [],
        });
      }
    } catch (err) {
      console.warn('Error fetching notifications:', err.message);
    } finally {
      setLoading(false);
    }
  };

  // Periodic polling for notifications and chat messages (every 15s)
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
      const interval = setInterval(() => {
        fetchChatSummary();
      }, 15000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const handleToggle = () => {
    const nextState = !isOpen;
    setIsOpen(nextState);
    if (nextState && isAuthenticated) {
      fetchNotifications();
    }
  };

  const markAsRead = async (id, e) => {
    e.stopPropagation();
    try {
      await api.put(`/notifications/${id}/read`, {});
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
      setUnreadNotifications((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error(err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all', {});
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadNotifications(0);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenDetail = async (item) => {
    setSelectedNotification(item);
    setIsOpen(false);
    if (!item.isRead) {
      try {
        await api.put(`/notifications/${item.id}/read`, {});
        setNotifications((prev) =>
          prev.map((n) => (n.id === item.id ? { ...n, isRead: true } : n))
        );
        setUnreadNotifications((prev) => Math.max(0, prev - 1));
      } catch (err) {
        console.error(err);
      }
    }
  };

  const renderStatusBadge = (status) => {
    if (!status) return null;
    switch (status.toUpperCase()) {
      case 'CONFIRMED':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">{currentLang === 'en' ? 'Confirmed' : currentLang === 'ru' ? 'Подтверждено' : 'Tasdiqlangan'}</span>;
      case 'PENDING':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/30">{currentLang === 'en' ? 'Pending' : currentLang === 'ru' ? 'В ожидании' : 'Kutilmoqda'}</span>;
      case 'CANCELLED':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-500/15 text-rose-400 border border-rose-500/30">{currentLang === 'en' ? 'Cancelled' : currentLang === 'ru' ? 'Отменено' : 'Bekor qilingan'}</span>;
      case 'COMPLETED':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/15 text-blue-400 border border-blue-500/30">{currentLang === 'en' ? 'Completed' : currentLang === 'ru' ? 'Выполнено' : 'Bajarilgan'}</span>;
      default:
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-500/15 text-slate-300 border border-slate-500/30">{status}</span>;
    }
  };

  const isMeetingReminder = (title = '', message = '') => {
    const text = `${title} ${message}`.toLowerCase();
    return (
      text.includes('uchrashuv') ||
      text.includes('majlis') ||
      text.includes('eslatma') ||
      text.includes('qold') ||
      text.includes('kundaligingizdagi') ||
      text.includes('sud majlisi')
    );
  };

  const getNotificationIcon = (title = '', message = '') => {
    const text = `${title} ${message}`.toLowerCase();
    // Reviews & Star ratings
    if (text.includes('baho') || text.includes('yulduz') || text.includes('sharh') || text.includes('fikr') || text.includes('reyting')) {
      return (
        <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/40 flex items-center justify-center text-amber-400 shrink-0 shadow-sm">
          <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
        </div>
      );
    }
    // Court hearings / Sud majlislari
    if (text.includes('majlis') || text.includes('sud') || text.includes('tergov') || text.includes('advokat')) {
      return (
        <div className="w-8 h-8 rounded-lg bg-[#c59b27]/15 border border-[#c59b27]/40 flex items-center justify-center text-[#c59b27] shrink-0 shadow-sm">
          <Scale className="w-4 h-4" />
        </div>
      );
    }
    // Appointments & Reminders
    if (text.includes('uchrashuv') || text.includes('qold') || text.includes('eslatma') || text.includes('soat') || text.includes('kun')) {
      return (
        <div className="w-8 h-8 rounded-lg bg-blue-500/15 border border-blue-500/40 flex items-center justify-center text-blue-400 shrink-0 shadow-sm">
          <Calendar className="w-4 h-4" />
        </div>
      );
    }
    // Confirmations
    if (text.includes('tasdiqlandi') || text.includes('tasdiqlash') || text.includes('qabul qilindi')) {
      return (
        <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-400 shrink-0 shadow-sm">
          <CheckCircle2 className="w-4 h-4" />
        </div>
      );
    }
    // Rejections
    if (text.includes('rad etildi') || text.includes('rad')) {
      return (
        <div className="w-8 h-8 rounded-lg bg-rose-500/15 border border-rose-500/40 flex items-center justify-center text-rose-400 shrink-0 shadow-sm">
          <XCircle className="w-4 h-4" />
        </div>
      );
    }
    // Cancellations
    if (text.includes('bekor qilindi') || text.includes('bekor')) {
      return (
        <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/40 flex items-center justify-center text-amber-400 shrink-0 shadow-sm">
          <AlertTriangle className="w-4 h-4" />
        </div>
      );
    }
    return (
      <div className="w-8 h-8 rounded-lg bg-[#15233c] border border-[#23385d] flex items-center justify-center text-[#c59b27] shrink-0 shadow-sm">
        <Bell className="w-4 h-4" />
      </div>
    );
  };

  const totalUnread = (unreadNotifications || 0) + (chatUnread.count || 0);

  const handleOpenChat = (chat) => {
    setIsOpen(false);
    setChatUnread((prev) => ({
      count: Math.max(0, prev.count - (chat.unreadCount || 1)),
      items: prev.items.filter((c) => c.conversationId !== chat.conversationId),
    }));
    if (user?.role === 'LAWYER') {
      navigate('/admin/lawyer-chat');
    } else {
      setActiveChatConvId(chat.conversationId);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        type="button"
        onClick={handleToggle}
        className={
          isLight
            ? "relative p-2 rounded-full hover:bg-slate-100 border border-slate-200 text-slate-600 hover:text-slate-900 transition-colors cursor-pointer"
            : "relative p-2 rounded-lg bg-[#0d1524] hover:bg-[#16233b] border border-slate-700/60 text-slate-300 hover:text-[#c59b27] transition-all cursor-pointer"
        }
        title={currentLang === 'en' ? 'Notifications & Chat' : currentLang === 'ru' ? 'Уведомления и Чат' : 'Bildirishnomalar va Chat'}
      >
        <Bell className={isLight ? "w-5 h-5" : "w-4 h-4"} />
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white shadow-md animate-pulse">
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={
            isLight
              ? "fixed left-3 right-3 sm:left-auto sm:right-0 top-18 sm:top-auto sm:mt-2 sm:w-96 max-w-sm sm:max-w-none mx-auto sm:mx-0 bg-white border border-slate-200 rounded-xl shadow-2xl shadow-slate-300/60 py-3 z-50 animate-in fade-in zoom-in-95 duration-150 text-slate-900"
              : "fixed left-3 right-3 sm:left-auto sm:right-0 top-18 sm:top-auto sm:mt-2 sm:w-96 max-w-sm sm:max-w-none mx-auto sm:mx-0 bg-[#070c16] border border-[#c59b27]/40 rounded-xl shadow-2xl shadow-black/90 py-3 z-50 animate-in fade-in zoom-in-95 duration-150 text-slate-200"
          }
        >
          {/* Header */}
          <div
            className={`px-4 pb-2 border-b flex items-center justify-between ${
              isLight ? 'border-slate-200' : 'border-[#1b2b46]'
            }`}
          >
            <div className="flex items-center space-x-2">
              <span className={`font-serif text-sm font-bold ${isLight ? 'text-slate-900' : 'text-slate-100'}`}>
                {currentLang === 'en' ? 'Notifications & Messages' : currentLang === 'ru' ? 'Уведомления и Сообщения' : 'Xabarnomalar & Muloqot'}
              </span>
              {totalUnread > 0 && (
                <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-[#c59b27]/20 text-[#c59b27] border border-[#c59b27]/40">
                  {totalUnread} {currentLang === 'en' ? 'new' : currentLang === 'ru' ? 'новых' : 'yangi'}
                </span>
              )}
            </div>

            {unreadNotifications > 0 && (
              <button
                type="button"
                onClick={markAllAsRead}
                className="text-[10px] text-[#c59b27] hover:text-[#d4af37] font-semibold flex items-center cursor-pointer"
              >
                <CheckCheck className="w-3 h-3 mr-1" />
                {currentLang === 'en' ? 'Mark all as read' : currentLang === 'ru' ? 'Прочитать все' : 'Barchasi o‘qildi'}
              </button>
            )}
          </div>

          {/* List */}
          <div
            className={`max-h-80 overflow-y-auto divide-y ${
              isLight ? 'divide-slate-100' : 'divide-[#141f33]/60'
            }`}
          >
            {!isAuthenticated ? (
              <div className="py-6 px-4 text-center">
                <Bell className={`w-8 h-8 mx-auto mb-2 opacity-50 ${isLight ? 'text-slate-400' : 'text-[#c59b27]'}`} />
                <h4 className={`text-xs font-bold mb-1 ${isLight ? 'text-slate-800' : 'text-slate-100'}`}>
                  {currentLang === 'en' ? 'Notification Center' : currentLang === 'ru' ? 'Центр Уведомлений' : 'Xabarnomalar Markazi'}
                </h4>
                <p className={`text-[11px] leading-relaxed mb-4 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                  {currentLang === 'en'
                    ? 'Sign in to track your appointment confirmation, SMS alerts, and status updates.'
                    : currentLang === 'ru'
                    ? 'Войдите в систему, чтобы просмотреть подтверждение заявки, SMS-отчеты и обновления.'
                    : 'Arizangiz tasdiqlanganligi, SMS hisoboti va yangilanishlarni ko‘rish uchun tizimga kiring.'}
                </p>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                      if (onAuthClick) onAuthClick();
                    }}
                    className="w-full py-2 px-3 rounded bg-[#c59b27] hover:bg-[#d4af37] text-slate-950 font-bold text-xs shadow transition-colors cursor-pointer"
                  >
                    {currentLang === 'en' ? 'Sign In / Register' : currentLang === 'ru' ? 'Войти / Регистрация' : 'Tizimga kirish / Ro‘yxatdan o‘tish'}
                  </button>
                  <a
                    href="/check-status"
                    onClick={() => setIsOpen(false)}
                    className={`block text-[11px] hover:underline pt-1 ${isLight ? 'text-slate-600' : 'text-slate-400'}`}
                  >
                    {currentLang === 'en' ? 'Check status via booking reference →' : currentLang === 'ru' ? 'Проверить статус по коду брони →' : 'Bron kodi orqali holatni tekshirish →'}
                  </a>
                </div>
              </div>
            ) : loading && notifications.length === 0 ? (
              <div className="py-8 text-center text-xs text-slate-400">
                {currentLang === 'en' ? 'Loading...' : currentLang === 'ru' ? 'Загрузка...' : 'Yuklanmoqda...'}
              </div>
            ) : notifications.length === 0 && chatUnread.items.length === 0 ? (
              <div className="py-8 text-center px-4">
                <Bell className={`w-8 h-8 mx-auto mb-2 opacity-40 ${isLight ? 'text-slate-400' : 'text-slate-600'}`} />
                <p className={`text-xs ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
                  {currentLang === 'en' ? 'No notifications yet' : currentLang === 'ru' ? 'Пока нет уведомлений' : 'Hozircha bildirishnomalar mavjud emas'}
                </p>
                <p className={`text-[10px] mt-1 ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>
                  {currentLang === 'en' ? 'You will be notified here and via SMS when status updates' : currentLang === 'ru' ? 'Вы получите уведомление здесь и по SMS при изменении статуса' : 'Arizalar holati o‘zgarganda shu yerda va SMS orqali xabar olasiz'}
                </p>
              </div>
            ) : (
              <>
                {chatUnread.items.map((chat) => (
                  <div
                    key={`chat-${chat.conversationId}`}
                    onClick={() => handleOpenChat(chat)}
                    className={`p-3.5 transition-colors relative flex items-start space-x-3 cursor-pointer group ${
                      isLight
                        ? 'bg-amber-100/70 hover:bg-amber-200/80 border-b border-amber-200'
                        : 'bg-[#0e1a30] hover:bg-[#142544] border-b border-[#1c3052]'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#c59b27]/20 border border-[#c59b27]/60 flex items-center justify-center text-[#c59b27] shrink-0 mt-0.5 shadow-sm">
                      <MessageSquare className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <h4 className={`text-xs font-bold truncate ${isLight ? 'text-slate-900' : 'text-slate-100'}`}>
                          {chat.senderName}
                        </h4>
                        <span className="px-1.5 py-0.2 rounded-full text-[9px] font-bold bg-[#c59b27] text-slate-950 shrink-0">
                          +{chat.unreadCount} yangi
                        </span>
                      </div>
                      <p className={`text-[11px] line-clamp-1 ${isLight ? 'text-slate-700' : 'text-slate-300'}`}>
                        {chat.lastMessage || 'Yangi xabar yuborildi'}
                      </p>
                      <span className="text-[10px] text-[#c59b27] hover:underline font-semibold mt-1 inline-block">
                        Chatni ochish va javob berish →
                      </span>
                    </div>
                  </div>
                ))}

                {notifications.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleOpenDetail(item)}
                  className={`p-3.5 transition-colors relative flex items-start space-x-3 cursor-pointer group ${
                    isLight
                      ? !item.isRead
                        ? 'bg-amber-50/50 hover:bg-amber-100/70'
                        : 'hover:bg-slate-100'
                      : !item.isRead
                      ? 'bg-[#0b1426]/70 hover:bg-[#101b30]'
                      : 'hover:bg-[#0c1424]'
                  }`}
                >
                  <div className="mt-0.5">
                    {getNotificationIcon(item.title, item.message)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <h4
                        className={`text-xs font-semibold truncate ${
                          isLight
                            ? !item.isRead ? 'text-slate-900 font-bold' : 'text-slate-700'
                            : !item.isRead ? 'text-slate-100' : 'text-slate-300'
                        }`}
                      >
                        {item.title}
                      </h4>
                      {!item.isRead && (
                        <button
                          type="button"
                          onClick={(e) => markAsRead(item.id, e)}
                          title={currentLang === 'en' ? 'Mark as read' : currentLang === 'ru' ? 'Отметить как прочитанное' : 'O‘qildi deb belgilash'}
                          className="ml-2 text-slate-400 hover:text-emerald-600 p-0.5 cursor-pointer"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    <p
                      className={`text-[11px] leading-relaxed font-light line-clamp-3 ${
                        isLight ? 'text-slate-600' : 'text-slate-400'
                      }`}
                    >
                      {(item.message || '').replace(/\[ESLATMA:[^\]]+\]/g, '').trim()}
                    </p>

                    <div className="flex items-center justify-between mt-2 pt-1 border-t text-[10px] border-[#141f33]/30">
                      <span className="text-[10px] text-[#c59b27] group-hover:underline font-medium inline-flex items-center">
                        {currentLang === 'en' ? 'Details →' : currentLang === 'ru' ? 'Подробнее →' : 'Tafsilotlar →'}
                      </span>
                      <span className="flex items-center text-slate-400">
                        <Clock className="w-3 h-3 mr-1" />
                        {new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>

                    <div
                      className={`flex items-center justify-between mt-1 text-[10px] ${
                        isLight ? 'text-slate-400' : 'text-slate-400'
                      }`}
                    >
                      <span className="text-[10px] text-slate-500">
                        {formatDate(item.createdAt)}
                      </span>

                      {(() => {
                        const isMeeting = isMeetingReminder(item.title, item.message);
                        const labelUnread = isMeeting
                          ? (currentLang === 'ru' ? 'Помню' : currentLang === 'en' ? 'Remembered' : 'Esimda')
                          : (currentLang === 'ru' ? 'Хорошо' : currentLang === 'en' ? 'Great' : 'Yaxshi');
                        const labelRead = isMeeting
                          ? (currentLang === 'ru' ? 'Помню ✓' : currentLang === 'en' ? 'Remembered ✓' : 'Esimda ✓')
                          : (currentLang === 'ru' ? 'Хорошо ✓' : currentLang === 'en' ? 'Great ✓' : 'Yaxshi ✓');

                        return !item.isRead ? (
                          <button
                            type="button"
                            onClick={(e) => markAsRead(item.id, e)}
                            className="px-2 py-0.5 rounded bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-500/40 text-[10px] font-semibold flex items-center shadow-xs transition-all cursor-pointer"
                            title={isMeeting ? (currentLang === 'ru' ? 'Напоминание принято' : currentLang === 'en' ? 'Reminder noted' : 'Eslatma qabul qilindi') : (currentLang === 'ru' ? 'Сообщение прочитано' : currentLang === 'en' ? 'Message read' : 'Xabar o‘qildi')}
                          >
                            <Check className="w-3 h-3 mr-1" />
                            {labelUnread}
                          </button>
                        ) : (
                          <span className="text-[10px] text-emerald-500/90 font-medium flex items-center">
                            <CheckCheck className="w-3 h-3 mr-1" />
                            {labelRead}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ))}
              </>
            )}
          </div>

          {/* Footer */}
          <div
            className={`px-4 pt-2 border-t text-center ${
              isLight ? 'border-slate-200' : 'border-[#1b2b46]'
            }`}
          >
            <span className={`text-[10px] ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
              {currentLang === 'en' ? 'SMS notifications:' : currentLang === 'ru' ? 'SMS-оповещения:' : 'SMS xabarnomalar:'} <span className={isLight ? 'text-slate-800 font-semibold font-mono' : 'text-slate-300 font-mono font-medium'}>+998 (97) 325-07-70</span>
            </span>
          </div>
        </div>
      )}

      {/* Selected Notification Full Detail Modal (User Requirement: Image 4) */}
      {selectedNotification && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity"
            onClick={() => setSelectedNotification(null)}
          />

          {/* Modal Container */}
          <div className="relative w-full max-w-lg bg-gradient-to-b from-[#0c1424] via-[#080d18] to-[#05080e] border border-[#c59b27]/40 rounded-2xl shadow-2xl shadow-black/95 overflow-hidden z-10 text-slate-200 animate-in zoom-in-95 duration-200">
            {/* Top Gold Accent */}
            <div className="h-1 w-full bg-gradient-to-r from-transparent via-[#c59b27] to-transparent" />

            {/* Header */}
            <div className="flex items-start justify-between p-4 sm:p-5 border-b border-[#1b2b46]/60">
              <div className="flex items-center space-x-3 min-w-0 pr-2">
                {getNotificationIcon(selectedNotification.title, selectedNotification.message)}
                <div className="min-w-0">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#c59b27] block truncate">
                    {t('notif_modal_badge', 'Bildirishnoma Tafsilotlari')}
                  </span>
                  <h3 className="font-serif text-base sm:text-lg font-bold text-slate-100 truncate">
                    {selectedNotification.title}
                  </h3>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setSelectedNotification(null)}
                className="w-8 h-8 rounded-full bg-[#142034] hover:bg-[#1c2c46] border border-slate-700/60 flex items-center justify-center text-slate-400 hover:text-white transition-colors shrink-0 cursor-pointer"
                aria-label="Yopish"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 sm:p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              {/* Timestamp & Status */}
              <div className="flex items-center justify-between text-xs text-slate-400 border-b border-[#142033] pb-3">
                <span className="flex items-center">
                  <Clock className="w-3.5 h-3.5 mr-1.5 text-[#c59b27]" />
                  {new Date(selectedNotification.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}, {formatDate(selectedNotification.createdAt)}
                </span>
                {renderStatusBadge(selectedNotification.appointment?.status)}
              </div>

              {/* Parsed / Linked Appointment Card */}
              {(() => {
                const apt = selectedNotification.appointment;
                const ref = apt?.bookingReference || selectedNotification.message?.match(/APT-\d{4}-\d{6}/i)?.[0];
                const client = apt?.clientName || selectedNotification.message?.match(/Mijoz\s+([^,.(]+)/i)?.[1]?.trim();
                const phone = apt?.clientPhone;
                const lawyer = apt?.lawyer?.fullName || selectedNotification.message?.match(/Advokat:\s*([^,.\n]+)/i)?.[1]?.trim();
                const service = apt?.service?.titleUz || apt?.service?.title;
                const date = apt?.appointmentDate || selectedNotification.message?.match(/Sana:\s*([^,.\n]+?)(?:\s+soat|\.)/i)?.[1]?.trim();
                const time = apt?.startTime || selectedNotification.message?.match(/soat\s*([0-9:]+)/i)?.[1]?.trim();

                if (!ref && !client && !lawyer) return null;

                return (
                  <div className="bg-[#09111e] border border-[#1b2b46] rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between border-b border-[#142033] pb-2.5">
                      <span className="text-[11px] uppercase tracking-wider text-[#c59b27] font-semibold flex items-center">
                        <FileText className="w-3.5 h-3.5 mr-1.5" />
                        {currentLang === 'en' ? 'Application & Appointment Details' : currentLang === 'ru' ? 'Детали Заявки и Приема' : 'Ariza & Qabul Ma’lumotlari'}
                      </span>
                      {ref && (
                        <span className="font-mono text-xs font-bold text-[#c59b27] bg-[#c59b27]/10 px-2 py-0.5 rounded border border-[#c59b27]/30">
                          {ref}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs">
                      {client && (
                        <div className="bg-[#0c1524] p-2.5 rounded-lg border border-[#162339]">
                          <span className="text-[10px] text-slate-400 block mb-0.5">
                            {currentLang === 'en' ? 'Client (Citizen):' : currentLang === 'ru' ? 'Клиент (Гражданин):' : 'Mijoz (Fuqaro):'}
                          </span>
                          <span className="font-semibold text-slate-200">{client}</span>
                        </div>
                      )}
                      {phone && (
                        <div className="bg-[#0c1524] p-2.5 rounded-lg border border-[#162339]">
                          <span className="text-[10px] text-slate-400 block mb-0.5">
                            {currentLang === 'en' ? 'Phone number:' : currentLang === 'ru' ? 'Номер телефона:' : 'Telefon raqam:'}
                          </span>
                          <span className="font-semibold text-slate-200 font-mono">{phone}</span>
                        </div>
                      )}
                      {lawyer && (
                        <div className="bg-[#0c1524] p-2.5 rounded-lg border border-[#162339]">
                          <span className="text-[10px] text-slate-400 block mb-0.5">
                            {currentLang === 'en' ? 'Assigned attorney:' : currentLang === 'ru' ? 'Назначенный адвокат:' : 'Biriktirilgan advokat:'}
                          </span>
                          <span className="font-semibold text-[#c59b27]">{lawyer}</span>
                        </div>
                      )}
                      {service && (
                        <div className="bg-[#0c1524] p-2.5 rounded-lg border border-[#162339]">
                          <span className="text-[10px] text-slate-400 block mb-0.5">
                            {currentLang === 'en' ? 'Legal area:' : currentLang === 'ru' ? 'Юридическое направление:' : 'Xizmat yo‘nalishi:'}
                          </span>
                          <span className="font-semibold text-slate-200">{service}</span>
                        </div>
                      )}
                      {(date || time) && (
                        <div className="bg-[#0c1524] p-2.5 rounded-lg border border-[#162339] sm:col-span-2">
                          <span className="text-[10px] text-slate-400 block mb-0.5">
                            {currentLang === 'en' ? 'Appointment time:' : currentLang === 'ru' ? 'Время приема:' : 'Uchrashuv vaqti:'}
                          </span>
                          <span className="font-semibold text-amber-200">{date} {time ? (currentLang === 'en' ? `at ${time}` : currentLang === 'ru' ? `в ${time}` : `soat ${time}`) : ''}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Full Message Body */}
              <div className="bg-[#070d17] border border-[#162238] rounded-xl p-4">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium block mb-1.5">
                  {currentLang === 'en' ? 'Notification text' : currentLang === 'ru' ? 'Текст уведомления' : 'Xabarnoma matni'}
                </span>
                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-light whitespace-pre-line">
                  {(selectedNotification.message || '').replace(/\[ESLATMA:[^\]]+\]/g, '').trim()}
                </p>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="p-4 sm:p-5 bg-[#070b14] border-t border-[#1b2b46]/70 flex flex-col sm:flex-row items-center justify-end gap-2.5">
              {(() => {
                const ref = selectedNotification.appointment?.bookingReference || selectedNotification.message?.match(/APT-\d{4}-\d{6}/i)?.[0];
                return (
                  <>
                    {ref && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedNotification(null);
                          navigate(`/check-status?ref=${ref}`);
                        }}
                        className="w-full sm:w-auto px-4 py-2 rounded-lg bg-[#c59b27] hover:bg-[#d4af37] text-slate-950 text-xs font-bold transition-all flex items-center justify-center cursor-pointer shadow-md"
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                        {currentLang === 'en' ? `Check appointment (${ref})` : currentLang === 'ru' ? `Проверить заявку (${ref})` : `Arizani tekshirish (${ref})`}
                      </button>
                    )}
                    {user && (user.role === 'ADMIN' || user.role === 'STAFF') && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedNotification(null);
                          navigate('/admin/appointments');
                        }}
                        className="w-full sm:w-auto px-3.5 py-2 rounded-lg bg-[#142033] hover:bg-[#1a2b44] border border-[#c59b27]/40 text-xs text-[#c59b27] font-semibold transition-all cursor-pointer"
                      >
                        {currentLang === 'en' ? 'View in Admin Panel' : currentLang === 'ru' ? 'В панель управления' : 'Admin panelda ko‘rish'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setSelectedNotification(null)}
                      className="w-full sm:w-auto px-4 py-2 rounded-lg border border-slate-700 hover:border-slate-500 text-xs text-slate-300 transition-colors cursor-pointer"
                    >
                      {currentLang === 'en' ? 'Close' : currentLang === 'ru' ? 'Закрыть' : 'Yopish'}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Client 1-on-1 Chat Modal triggered directly from Smart Bell */}
      {activeChatConvId && (
        <ClientChatModal
          isOpen={!!activeChatConvId}
          onClose={() => setActiveChatConvId(null)}
          initialConversationId={activeChatConvId}
        />
      )}
    </div>
  );
}
